import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdminTopline, Panel } from "../../components/console";
import { Download, X } from "../../components/icons";
import { api, authHeaders, describeError } from "../../lib/api";
import type { RefItem } from "../../lib/api";
import { getCurrentRole } from "../../lib/auth";
import { ROLE_LABEL_LO } from "../../lib/roles";
import { buildXlsx, saveBlob } from "../../lib/xlsx";

/**
 * จัดการ ຕາແສງ (super admin เท่านั้น — route อยู่ใต้ tier "system", ADR-0017)
 *
 * ตาแสงหนึ่งคือจุดบริการที่ดูแลหลายบ้านในเมืองเดียวกัน: เลือกแขวง → เมือง (ชิป) แล้วเลือก
 * ตาแสงทางซ้ายเพื่อติ๊กบ้านที่ขึ้นกับตาแสงนั้นทางขวา · บ้านหนึ่งอยู่ได้ตาแสงเดียว
 * เปิดมาเลือกให้ก่อน: ນະຄອນຫຼວງວຽງຈັນ → เมืองแรก → ตาแสงแรก (ไม่มีจอว่างตอนเข้า)
 * ติ๊กบ้านที่อยู่ตาแสงอื่น = ย้ายมา (จอเตือนก่อนบันทึก)
 *
 * อ่านจาก /v1/reference/* แต่ข้าม cache ของเบราว์เซอร์ เพราะหลังแก้ต้องเห็นค่าใหม่ทันที
 */

const noStore = () => ({ headers: authHeaders(), cache: "no-store" as const });

function unwrap(res: { data?: { items: RefItem[] }; error?: Parameters<typeof describeError>[0]; response: Response }): RefItem[] {
  if (res.error || !res.data) throw new Error(describeError(res.error, res.response.status));
  return res.data.items;
}

/** แขวงที่เปิดมาเจอก่อน — ນະຄອນຫຼວງວຽງຈັນ (รหัสในระเบียบ 01, ดู apps/api/data/lao_admin.csv) */
const DEFAULT_PROVINCE_CODE = "01";

export function TasaengsPage() {
  const qc = useQueryClient();
  const role = getCurrentRole();
  // null = ยังไม่ได้เลือกเอง → ใช้ນະຄອນຫຼວງວຽງຈັນ (รหัส 01) เป็นค่าตั้งต้น · "" = เลือก "ເລືອກແຂວງ" เอง
  const [pickedProvince, setProvinceID] = useState<string | null>(null);
  // null = ยังไม่ได้เลือกเอง → เมืองแรกของแขวง / ตาแสงแรกของเมือง (เข้ามาแล้วเห็นข้อมูลเลย ไม่ใช่จอว่าง)
  const [pickedDistrict, setDistrictID] = useState<string | null>(null);
  const [pickedTasaeng, setTasaengID] = useState<string | null>(null);
  const [editing, setEditing] = useState<{ item: RefItem | null } | null>(null);
  // item null = เพิ่มบ้านใหม่, มีค่า = แก้บ้านนั้น
  const [villageForm, setVillageForm] = useState<{ item: RefItem | null } | null>(null);
  const [error, setError] = useState<string | null>(null);
  // ตาแสงที่เพิ่งบันทึกบ้านสำเร็จ ไว้ขึ้นข้อความ "ບັນທຶກແລ້ວ" หลัง panel ถูก remount
  const [savedFor, setSavedFor] = useState<string | null>(null);

  const provinces = useQuery({
    queryKey: ["tasaeng-admin", "provinces"],
    queryFn: async () => unwrap(await api.GET("/v1/reference/provinces", noStore())),
  });
  const provinceID = pickedProvince ?? provinces.data?.find((p) => p.code === DEFAULT_PROVINCE_CODE)?.id ?? "";
  const districts = useQuery({
    queryKey: ["tasaeng-admin", "districts", provinceID],
    queryFn: async () =>
      unwrap(await api.GET("/v1/reference/districts", { ...noStore(), params: { query: { province_id: provinceID } } })),
    enabled: provinceID !== "",
  });
  const districtID = pickedDistrict ?? districts.data?.[0]?.id ?? "";
  const tasaengs = useQuery({
    queryKey: ["tasaeng-admin", "tasaengs", districtID],
    queryFn: async () =>
      unwrap(await api.GET("/v1/reference/tasaengs", { ...noStore(), params: { query: { district_id: districtID } } })),
    enabled: districtID !== "",
  });
  const villages = useQuery({
    queryKey: ["tasaeng-admin", "villages", districtID],
    queryFn: async () =>
      unwrap(await api.GET("/v1/reference/villages", { ...noStore(), params: { query: { district_id: districtID } } })),
    enabled: districtID !== "",
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["tasaeng-admin"] });
    void qc.invalidateQueries({ queryKey: ["reference"] });
  };

  const remove = useMutation({
    mutationFn: async (id: string) => {
      const { error: err, response } = await api.DELETE("/v1/admin/tasaengs/{id}", {
        params: { path: { id } },
        headers: authHeaders(),
      });
      if (err) throw new Error(describeError(err, response.status));
    },
    onSuccess: (_, id) => {
      setError(null);
      if (id === tasaengID) setTasaengID(null);
      refresh();
    },
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  const removeVillage = useMutation({
    mutationFn: async (id: string) => {
      const { error: err, response } = await api.DELETE("/v1/admin/villages/{id}", {
        params: { path: { id } },
        headers: authHeaders(),
      });
      if (err) throw new Error(describeError(err, response.status));
    },
    onSuccess: () => {
      setError(null);
      refresh();
    },
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  function confirmRemoveVillage(v: RefItem) {
    const t = v.tasaeng_id ? tasaengRows.find((x) => x.id === v.tasaeng_id) : undefined;
    const warn = t ? `\nບ້ານນີ້ຈະອອກຈາກຕາແສງ ${t.name_lo} ແລະ ເຈົ້າໜ້າທີ່ຕາແສງຈະບໍ່ເຫັນຄຳຮ້ອງໃໝ່ຈາກບ້ານນີ້` : "";
    if (
      window.confirm(
        `ລຶບບ້ານ ${v.name_lo} (${v.code}) ແທ້ບໍ?${warn}\nບ້ານນີ້ຈະຫາຍຈາກລາຍການເລືອກທີ່ຢູ່ — ຄຳຮ້ອງເກົ່າຍັງສະແດງຊື່ບ້ານໄດ້ຄືເດີມ`,
      )
    )
      removeVillage.mutate(v.id);
  }

  function confirmRemove(item: RefItem) {
    const count = memberCount.get(item.id) ?? 0;
    const warn =
      count > 0 ? `\nບ້ານ ${String(count)} ບ້ານຈະບໍ່ຂຶ້ນກັບຕາແສງໃດ ແລະ ເຈົ້າໜ້າທີ່ຂອງຕາແສງນີ້ຈະເບິ່ງຄຳຮ້ອງບໍ່ເຫັນ` : "";
    if (window.confirm(`ລຶບຕາແສງ ${item.name_lo} (${item.code}) ແທ້ບໍ?${warn}`)) remove.mutate(item.id);
  }

  const tasaengRows = tasaengs.data ?? [];
  const villageRows = villages.data ?? [];
  const memberCount = new Map<string, number>();
  for (const v of villageRows) if (v.tasaeng_id) memberCount.set(v.tasaeng_id, (memberCount.get(v.tasaeng_id) ?? 0) + 1);
  const unassigned = villageRows.filter((v) => !v.tasaeng_id).length;
  const tasaengID = pickedTasaeng ?? tasaengRows[0]?.id ?? null;
  const selected = tasaengRows.find((t) => t.id === tasaengID) ?? null;
  const members = selected ? villageRows.filter((v) => v.tasaeng_id === selected.id).map((v) => v.id) : [];
  // key เปลี่ยน = ตั้งค่าติ๊กใหม่จากสมาชิกจริง (เปลี่ยนตาแสง หรือโหลดใหม่หลังบันทึก)
  const panelKey = `${selected?.id ?? ""}:${String(selected?.version ?? 0)}:${members.join(",")}`;
  const districtName = districts.data?.find((d) => d.id === districtID)?.name_lo;
  const savedKey = savedFor !== null && savedFor === selected?.id ? panelKey : null;
  const provinceName = provinces.data?.find((p) => p.id === provinceID)?.name_lo ?? "";
  const [exporting, setExporting] = useState(false);

  return (
    <div>
      <AdminTopline
        title="ຕາແສງ ແລະ ບ້ານ"
        subtitle="ຕາແສງໜຶ່ງເປັນຈຸດບໍລິການທີ່ດູແລຫຼາຍບ້ານ — ເລືອກເມືອງ ແລ້ວກຳນົດວ່າແຕ່ລະຕາແສງດູແລບ້ານໃດແດ່"
        roleLabel={role ? ROLE_LABEL_LO[role] : undefined}
        actions={
          <button
            type="button"
            className="btn-outline btn-sm"
            disabled={!districts.data?.length}
            onClick={() => {
              setExporting(true);
            }}
          >
            <Download size={14} /> ສົ່ງອອກ Excel
          </button>
        }
      />

      {/* ① ແຂວງ/ເມືອງ: เมืองเป็นชิปให้เห็นทั้งหมดในครั้งเดียว (เดิมซ่อนใน dropdown แล้วจอว่าง) */}
      <div className="tsg-toolbar">
        <label className="tsg-toolbar__province">
          <span className="caption">ແຂວງ</span>
          <select
            className="filter-select"
            value={provinceID}
            aria-label="ແຂວງ"
            onChange={(e) => {
              setProvinceID(e.target.value);
              setDistrictID(null);
              setTasaengID(null);
            }}
          >
            <option value="">ເລືອກແຂວງ</option>
            {(provinces.data ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.name_lo}
              </option>
            ))}
          </select>
        </label>
        <div className="tsg-toolbar__districts">
          <span className="caption">ເມືອງ</span>
          {provinceID === "" ? (
            <span className="caption">ເລືອກແຂວງກ່ອນ</span>
          ) : districts.isPending ? (
            <span className="caption">ກຳລັງໂຫຼດ…</span>
          ) : (
            <div className="group-chips" role="group" aria-label="ເມືອງ" style={{ marginBottom: 0 }}>
              {(districts.data ?? []).map((d) => (
                <button
                  key={d.id}
                  type="button"
                  className={d.id === districtID ? "group-chip is-active" : "group-chip"}
                  aria-pressed={d.id === districtID}
                  onClick={() => {
                    setDistrictID(d.id);
                    setTasaengID(null);
                  }}
                >
                  {d.name_lo}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* สรุปของเมืองที่เลือก — บ้านที่ยังไม่มีตาแสงคือสิ่งที่ต้องจัดการ (คำร้องจากบ้านนั้นไม่มีตาแสงรับ) */}
      {districtID ? (
        <div className="kpi-grid">
          <article className="kpi">
            <div className="lbl">ຕາແສງໃນເມືອງ{districtName ?? ""}</div>
            <div className="val num">{tasaengs.isPending ? "…" : tasaengRows.length}</div>
          </article>
          <article className="kpi">
            <div className="lbl">ບ້ານທັງໝົດ</div>
            <div className="val num">{villages.isPending ? "…" : villageRows.length}</div>
          </article>
          <article className="kpi">
            <div className="lbl">ຂຶ້ນກັບຕາແສງແລ້ວ</div>
            <div className="val num">{villages.isPending ? "…" : villageRows.length - unassigned}</div>
          </article>
          <article className={unassigned > 0 ? "kpi kpi--warn" : "kpi"}>
            <div className="lbl">ຍັງບໍ່ມີຕາແສງ</div>
            <div className="val num">{villages.isPending ? "…" : unassigned}</div>
            {unassigned > 0 ? <div className="delta warn">ຄຳຮ້ອງຈາກບ້ານເຫຼົ່ານີ້ບໍ່ມີຕາແສງຮັບ</div> : null}
          </article>
        </div>
      ) : null}

      {error ? (
        <div className="alert-error" style={{ marginBottom: "var(--space-4)" }}>
          {error}
        </div>
      ) : null}

      <div className="grid2">
        <Panel
          title={districtName ? `① ເລືອກຕາແສງ · ເມືອງ${districtName}` : "① ເລືອກຕາແສງ"}
        >
          <div className="panel__actions">
            <button
              type="button"
              className="btn-secondary btn-sm"
              disabled={districtID === ""}
              onClick={() => {
                setEditing({ item: null });
              }}
            >
              + ເພີ່ມຕາແສງ
            </button>
          </div>
          <div className="table-scroll unit-list">
            <table className="data-table">
              <thead>
                <tr>
                  <th>ລະຫັດ</th>
                  <th>ຊື່ຕາແສງ</th>
                  <th className="is-right">ບ້ານ</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {districtID === "" ? (
                  <EmptyRow cols={4}>{provinceID === "" ? "ເລືອກແຂວງກ່ອນ" : "ແຂວງນີ້ຍັງບໍ່ມີເມືອງ"}</EmptyRow>
                ) : tasaengs.isPending ? (
                  <EmptyRow cols={4}>ກຳລັງໂຫຼດ…</EmptyRow>
                ) : tasaengs.error ? (
                  <EmptyRow cols={4}>ໂຫຼດບໍ່ສຳເລັດ: {tasaengs.error.message}</EmptyRow>
                ) : tasaengRows.length === 0 ? (
                  <EmptyRow cols={4}>ເມືອງນີ້ຍັງບໍ່ມີຕາແສງ — ກົດ “+ ເພີ່ມຕາແສງ” ເພື່ອສ້າງຕາແສງທຳອິດ</EmptyRow>
                ) : (
                  tasaengRows.map((t) => (
                    <tr
                      key={t.id}
                      className={["is-selectable", t.id === tasaengID ? "is-selected" : ""].join(" ")}
                      aria-selected={t.id === tasaengID}
                      onClick={() => {
                        setTasaengID(t.id);
                        setError(null);
                      }}
                    >
                      <td className="num">{t.code}</td>
                      <td className="body-sm" style={{ fontWeight: 500 }}>
                        {t.name_lo}
                      </td>
                      <td className="is-right num">{memberCount.get(t.id) ?? 0}</td>
                      <td className="is-right">
                        <div className="row" style={{ gap: "var(--space-3)", justifyContent: "flex-end" }}>
                          <button
                            type="button"
                            className="link-action"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditing({ item: t });
                            }}
                          >
                            ແກ້ໄຂ
                          </button>
                          <button
                            type="button"
                            className="link-action"
                            disabled={remove.isPending}
                            onClick={(e) => {
                              e.stopPropagation();
                              confirmRemove(t);
                            }}
                          >
                            ລຶບ
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Panel>

        <MembershipPanel
          key={panelKey}
          districtID={districtID}
          members={members}
          saved={savedKey === panelKey}
          tasaeng={selected}
          tasaengs={tasaengRows}
          villages={villageRows}
          loading={villages.isPending && districtID !== ""}
          deleting={removeVillage.isPending}
          onAddVillage={() => {
            setVillageForm({ item: null });
          }}
          onEditVillage={(v) => {
            setVillageForm({ item: v });
          }}
          onDeleteVillage={confirmRemoveVillage}
          onSaved={(failure) => {
            // ข้อความ error อยู่ที่หน้า ไม่ใช่ใน panel เพราะ panel ถูก remount หลังโหลดใหม่
            setError(failure);
            setSavedFor(failure === null ? (selected?.id ?? null) : null);
            refresh();
          }}
        />
      </div>

      {editing ? (
        <TasaengModal
          districtID={districtID}
          item={editing.item}
          onClose={() => {
            setEditing(null);
          }}
          onDone={(id) => {
            setEditing(null);
            setError(null);
            setTasaengID(id);
            refresh();
          }}
        />
      ) : null}
      {exporting ? (
        <ExportModal
          provinceName={provinceName}
          districts={districts.data ?? []}
          initial={districtID}
          onClose={() => {
            setExporting(false);
          }}
        />
      ) : null}
      {villageForm ? (
        <VillageModal
          districtID={districtID}
          item={villageForm.item}
          onClose={() => {
            setVillageForm(null);
          }}
          onDone={() => {
            setVillageForm(null);
            setError(null);
            refresh();
          }}
        />
      ) : null}
    </div>
  );
}

interface DistrictData {
  district: RefItem;
  tasaengs: RefItem[];
  villages: RefItem[];
}

/**
 * เลือกเมือง (หลายเมืองได้) ของแขวงที่เปิดอยู่ แล้วส่งออกเป็น .xlsx ไฟล์เดียว
 * ดึงตาแสง/บ้านของแต่ละเมืองผ่าน queryKey เดียวกับจอ — ใช้ cache ที่ยังสดอยู่ (staleTime) ที่เหลือโหลดใหม่
 */
function ExportModal({
  provinceName,
  districts,
  initial,
  onClose,
}: {
  provinceName: string;
  districts: RefItem[];
  initial: string;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [picked, setPicked] = useState<Set<string>>(() => new Set(initial ? [initial] : []));
  const [error, setError] = useState<string | null>(null);
  const allPicked = districts.length > 0 && districts.every((d) => picked.has(d.id));

  const run = useMutation({
    mutationFn: async () => {
      const chosen = districts.filter((d) => picked.has(d.id));
      const data = await Promise.all(
        chosen.map(async (d): Promise<DistrictData> => {
          const [tasaengs, villages] = await Promise.all([
            qc.fetchQuery({
              queryKey: ["tasaeng-admin", "tasaengs", d.id],
              queryFn: async () =>
                unwrap(await api.GET("/v1/reference/tasaengs", { ...noStore(), params: { query: { district_id: d.id } } })),
            }),
            qc.fetchQuery({
              queryKey: ["tasaeng-admin", "villages", d.id],
              queryFn: async () =>
                unwrap(await api.GET("/v1/reference/villages", { ...noStore(), params: { query: { district_id: d.id } } })),
            }),
          ]);
          return { district: d, tasaengs, villages };
        }),
      );
      exportXlsx(provinceName, data, allPicked);
    },
    onSuccess: onClose,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  function toggle(id: string) {
    setPicked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="ສົ່ງອອກ Excel">
      <div className="modal modal--sm">
        <div className="modal__header">
          <h2 className="modal__title">ສົ່ງອອກ Excel</h2>
          <button type="button" className="btn-outline" onClick={onClose} aria-label="ປິດ">
            <X size={16} />
          </button>
        </div>
        <div className="stack">
          <p className="caption">ແຂວງ{provinceName} — ເລືອກເມືອງທີ່ຕ້ອງການ (ເລືອກໄດ້ຫຼາຍເມືອງ)</p>
          <label className="row body-sm" style={{ gap: "var(--space-2)", fontWeight: 500 }}>
            <input
              type="checkbox"
              checked={allPicked}
              onChange={(e) => {
                setPicked(new Set(e.target.checked ? districts.map((d) => d.id) : []));
              }}
            />
            ເລືອກທັງໝົດ ({districts.length} ເມືອງ)
          </label>
          <div className="group-chips" role="group" aria-label="ເມືອງທີ່ຈະສົ່ງອອກ" style={{ marginBottom: 0 }}>
            {districts.map((d) => (
              <button
                key={d.id}
                type="button"
                className={picked.has(d.id) ? "group-chip is-active" : "group-chip"}
                aria-pressed={picked.has(d.id)}
                onClick={() => {
                  toggle(d.id);
                }}
              >
                {d.name_lo}
              </button>
            ))}
          </div>
          <p className="caption">ສົ່ງອອກຄ່າທີ່ບັນທຶກແລ້ວເທົ່ານັ້ນ — ບ້ານທີ່ຕິກໄວ້ແຕ່ຍັງບໍ່ໄດ້ບັນທຶກຈະບໍ່ຢູ່ໃນໄຟລ໌</p>
        </div>
        {error ? <div className="alert-error">{error}</div> : null}
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={run.isPending}>
            ຍົກເລີກ
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={picked.size === 0 || run.isPending}
            onClick={() => {
              setError(null);
              run.mutate();
            }}
          >
            {run.isPending ? "ກຳລັງກຽມໄຟລ໌…" : `ສົ່ງອອກ ${String(picked.size)} ເມືອງ`}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * เขียน .xlsx จากเมืองที่เลือก — ค่าที่ server มีตอนนี้ (ไม่รวมติ๊กที่ยังไม่ได้บันทึก)
 * sheet ບ້ານ: ทุกบ้าน เรียงตามเมือง → ตาแสง (บ้านที่ยังไม่มีตาแสงอยู่ท้ายของเมือง) · sheet ຕາແສງ: สรุปจำนวนบ้าน
 */
function exportXlsx(provinceName: string, data: DistrictData[], wholeProvince: boolean) {
  const byCode = (a: RefItem, b: RefItem) => a.code.localeCompare(b.code);
  const villageRows: (string | number)[][] = [];
  const tasaengRows: (string | number)[][] = [];

  for (const { district, tasaengs, villages } of data) {
    const dName = district.name_lo;
    const byID = new Map(tasaengs.map((t) => [t.id, t]));
    const count = new Map<string, number>();
    for (const v of villages) if (v.tasaeng_id) count.set(v.tasaeng_id, (count.get(v.tasaeng_id) ?? 0) + 1);
    const tasaengOf = (v: RefItem) => (v.tasaeng_id ? byID.get(v.tasaeng_id) : undefined);

    for (const v of [...villages].sort((a, b) => {
      const ta = tasaengOf(a);
      const tb = tasaengOf(b);
      if (!ta !== !tb) return ta ? -1 : 1;
      return (ta && tb ? byCode(ta, tb) : 0) || byCode(a, b);
    })) {
      const t = tasaengOf(v);
      villageRows.push([provinceName, dName, v.code, v.name_lo, t?.code ?? "", t?.name_lo ?? "ຍັງບໍ່ມີຕາແສງ"]);
    }
    for (const t of [...tasaengs].sort(byCode)) {
      tasaengRows.push([provinceName, dName, t.code, t.name_lo, count.get(t.id) ?? 0]);
    }
    const unassigned = villages.filter((v) => !v.tasaeng_id).length;
    if (unassigned > 0) tasaengRows.push([provinceName, dName, "", "ຍັງບໍ່ມີຕາແສງ", unassigned]);
  }

  const blob = buildXlsx([
    {
      name: "ບ້ານ",
      header: ["ແຂວງ", "ເມືອງ", "ລະຫັດບ້ານ", "ຊື່ບ້ານ", "ລະຫັດຕາແສງ", "ຊື່ຕາແສງ"],
      rows: villageRows,
      widths: [22, 18, 14, 26, 14, 22],
    },
    {
      name: "ຕາແສງ",
      header: ["ແຂວງ", "ເມືອງ", "ລະຫັດຕາແສງ", "ຊື່ຕາແສງ", "ຈຳນວນບ້ານ"],
      rows: tasaengRows,
      widths: [22, 18, 14, 22, 12],
    },
  ]);
  const d = new Date();
  const date = `${String(d.getFullYear())}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const scope =
    wholeProvince || data.length === 0
      ? `ແຂວງ${provinceName}`
      : data.length === 1
        ? `ເມືອງ${data[0]?.district.name_lo ?? ""}`
        : `${String(data.length)}ເມືອງ`;
  saveBlob(blob, `ຕາແສງ-ບ້ານ_${scope}_${date}.xlsx`);
}

function EmptyRow({ cols, children }: { cols: number; children: ReactNode }) {
  return (
    <tr>
      <td colSpan={cols} className="data-table__empty">
        {children}
      </td>
    </tr>
  );
}

/** ทางขวา: ติ๊กบ้านของเมืองนี้ที่ขึ้นกับตาแสงที่เลือก แล้วบันทึกทั้งชุด */
function MembershipPanel({
  districtID,
  members,
  saved,
  tasaeng,
  tasaengs,
  villages,
  loading,
  deleting,
  onAddVillage,
  onEditVillage,
  onDeleteVillage,
  onSaved,
}: {
  districtID: string;
  members: string[];
  saved: boolean;
  tasaeng: RefItem | null;
  tasaengs: RefItem[];
  villages: RefItem[];
  loading: boolean;
  deleting: boolean;
  onAddVillage: () => void;
  onEditVillage: (v: RefItem) => void;
  onDeleteVillage: (v: RefItem) => void;
  /** null = สำเร็จ, ข้อความ = ล้มเหลว */
  onSaved: (failure: string | null) => void;
}) {
  // ผู้เรียกใส่ key ให้ panel นี้ ค่าติ๊กจึงเริ่มจากสมาชิกจริงเสมอโดยไม่ต้องใช้ effect
  const serverMembers = new Set(members);
  const [checked, setChecked] = useState<Set<string>>(() => new Set(members));
  const [search, setSearch] = useState("");
  // ช่วยไล่บ้านที่ยังไม่มีตาแสง — บ้านที่ติ๊กไว้ยังแสดงอยู่ จะได้เห็นว่ากำลังจะบันทึกอะไร
  const [onlyUnassigned, setOnlyUnassigned] = useState(false);

  const nameOf = new Map(tasaengs.map((t) => [t.id, t.name_lo]));
  const dirty = checked.size !== serverMembers.size || [...checked].some((id) => !serverMembers.has(id));
  const moving = villages.filter((v) => checked.has(v.id) && v.tasaeng_id && v.tasaeng_id !== tasaeng?.id);
  const q = search.trim().toLowerCase();
  const shown = villages
    .filter((v) => q === "" || v.name_lo.toLowerCase().includes(q) || v.code.includes(q))
    .filter((v) => !onlyUnassigned || !v.tasaeng_id || checked.has(v.id));

  const save = useMutation({
    mutationFn: async () => {
      if (!tasaeng) return;
      const { error: err, response } = await api.PUT("/v1/admin/tasaengs/{id}/villages", {
        params: { path: { id: tasaeng.id }, header: { "If-Match": String(tasaeng.version) } },
        body: { village_ids: [...checked] },
        headers: authHeaders(),
      });
      if (err) {
        throw new Error(
          response.status === 409
            ? "ມີຄົນແກ້ໄຂຕາແສງນີ້ກ່ອນແລ້ວ — ໂຫຼດຄ່າລ່າສຸດໃຫ້ແລ້ວ ກະລຸນາກວດແລ້ວບັນທຶກອີກຄັ້ງ"
            : describeError(err, response.status),
        );
      }
    },
    onSuccess: () => {
      onSaved(null);
    },
    onError: (e: Error) => {
      // 409 = ต้องได้ version ใหม่ก่อนกดซ้ำ; ค่าที่ติ๊กไว้หายเพราะ panel ถูก remount
      // ซึ่งตั้งใจ — ผู้ใช้ต้องเห็นสมาชิกล่าสุดก่อนตัดสินใหม่
      onSaved(e.message);
    },
  });

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const title = tasaeng ? `② ຕິກບ້ານທີ່ຂຶ້ນກັບຕາແສງ ${tasaeng.name_lo}` : "② ຕິກບ້ານທີ່ຂຶ້ນກັບຕາແສງ";

  return (
    <Panel title={title} note={tasaeng ? `ເລືອກແລ້ວ ${String(checked.size)} ບ້ານ` : undefined}>
      <div className="filter-bar" style={{ marginBottom: "var(--space-3)" }}>
        <input
          type="search"
          className="filter-search"
          placeholder="ຊອກຫາບ້ານ"
          aria-label="ຊອກຫາບ້ານ"
          value={search}
          disabled={districtID === ""}
          onChange={(e) => {
            setSearch(e.target.value);
          }}
        />
        <label className="row body-sm" style={{ gap: "var(--space-2)", whiteSpace: "nowrap" }}>
          <input
            type="checkbox"
            checked={onlyUnassigned}
            disabled={!tasaeng}
            onChange={(e) => {
              setOnlyUnassigned(e.target.checked);
            }}
          />
          ສະເພາະບ້ານທີ່ຍັງບໍ່ມີຕາແສງ
        </label>
        <button type="button" className="btn-secondary btn-sm" disabled={districtID === ""} onClick={onAddVillage}>
          + ເພີ່ມບ້ານ
        </button>
      </div>
      <div className="table-scroll unit-list">
        <table className="data-table">
          <thead>
            <tr>
              <th style={{ width: "3rem" }} />
              <th>ລະຫັດ</th>
              <th>ຊື່ບ້ານ</th>
              <th>ຕາແສງປັດຈຸບັນ</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {districtID === "" ? (
              <EmptyRow cols={5}>ເລືອກເມືອງກ່ອນ</EmptyRow>
            ) : !tasaeng ? (
              <EmptyRow cols={5}>ສ້າງ ຫຼື ເລືອກຕາແສງທາງຊ້າຍກ່ອນ ແລ້ວຕິກບ້ານທີ່ຂຶ້ນກັບຕາແສງນັ້ນຢູ່ບ່ອນນີ້</EmptyRow>
            ) : loading ? (
              <EmptyRow cols={5}>ກຳລັງໂຫຼດ…</EmptyRow>
            ) : shown.length === 0 ? (
              <EmptyRow cols={5}>
                {villages.length === 0
                  ? "ເມືອງນີ້ຍັງບໍ່ມີບ້ານ — ກົດ “+ ເພີ່ມບ້ານ”"
                  : onlyUnassigned && q === ""
                    ? "ທຸກບ້ານຂຶ້ນກັບຕາແສງແລ້ວ"
                    : "ບໍ່ພົບບ້ານທີ່ຊອກຫາ"}
              </EmptyRow>
            ) : (
              shown.map((v) => {
                const inputID = `village-${v.id}`;
                return (
                  <tr
                    key={v.id}
                    className={["is-selectable", checked.has(v.id) ? "is-selected" : ""].join(" ")}
                    onClick={() => {
                      toggle(v.id);
                    }}
                  >
                    <td>
                      <input
                        id={inputID}
                        type="checkbox"
                        checked={checked.has(v.id)}
                        onClick={(e) => {
                          e.stopPropagation();
                        }}
                        onChange={() => {
                          toggle(v.id);
                        }}
                      />
                    </td>
                    <td className="num">{v.code}</td>
                    <td>
                      <label htmlFor={inputID} className="body-sm" style={{ fontWeight: 500, cursor: "pointer" }}>
                        {v.name_lo}
                      </label>
                    </td>
                    <td>
                      <MemberBadge current={v.tasaeng_id} selected={tasaeng.id} names={nameOf} />
                    </td>
                    <td className="is-right">
                      <div className="row" style={{ gap: "var(--space-3)", justifyContent: "flex-end" }}>
                        <button
                          type="button"
                          className="link-action"
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditVillage(v);
                          }}
                        >
                          ແກ້ໄຂ
                        </button>
                        <button
                          type="button"
                          className="link-action"
                          disabled={deleting}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDeleteVillage(v);
                          }}
                        >
                          ລຶບ
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {moving.length > 0 ? (
        <div className="alert-warning" style={{ marginTop: "var(--space-3)" }}>
          ຈະຍ້າຍ {moving.length} ບ້ານອອກຈາກຕາແສງເດີມ:{" "}
          {moving.map((v) => `${v.name_lo} (${nameOf.get(v.tasaeng_id ?? "") ?? "—"})`).join(", ")}
        </div>
      ) : null}
      <div className="row" style={{ justifyContent: "flex-end", gap: "var(--space-3)", marginTop: "var(--space-4)" }}>
        {saved && !dirty ? <span className="caption">ບັນທຶກແລ້ວ</span> : null}
        <button
          type="button"
          className="btn-secondary"
          disabled={!dirty || save.isPending}
          onClick={() => {
            setChecked(new Set(serverMembers));
          }}
        >
          ຍົກເລີກການປ່ຽນແປງ
        </button>
        <button
          type="button"
          className="btn-primary"
          disabled={!tasaeng || !dirty || save.isPending}
          onClick={() => {
            save.mutate();
          }}
        >
          {save.isPending ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກບ້ານໃນຕາແສງ"}
        </button>
      </div>
    </Panel>
  );
}

function MemberBadge({
  current,
  selected,
  names,
}: {
  current: string | undefined;
  selected: string;
  names: Map<string, string>;
}) {
  if (!current) return <span className="status-badge status-badge--pending">ຍັງບໍ່ມີຕາແສງ</span>;
  if (current === selected) return <span className="status-badge status-badge--success">ຕາແສງນີ້</span>;
  return <span className="status-badge status-badge--info">{names.get(current) ?? "ຕາແສງອື່ນ"}</span>;
}

function TasaengModal({
  districtID,
  item,
  onClose,
  onDone,
}: {
  districtID: string;
  item: RefItem | null;
  onClose: () => void;
  onDone: (id: string) => void;
}) {
  const [code, setCode] = useState(item?.code ?? "");
  const [nameLO, setNameLO] = useState(item?.name_lo ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async (): Promise<string> => {
      const body = { district_id: item?.parent_id ?? districtID, code: code.trim(), name_lo: nameLO.trim() };
      const headers = authHeaders();
      const res = item
        ? await api.PUT("/v1/admin/tasaengs/{id}", {
            params: { path: { id: item.id }, header: { "If-Match": String(item.version) } },
            body,
            headers,
          })
        : await api.POST("/v1/admin/tasaengs", { body, headers });
      if (res.error || !res.data) {
        throw new Error(
          res.response.status === 409
            ? item
              ? "ມີຄົນແກ້ໄຂຕາແສງນີ້ກ່ອນແລ້ວ ຫຼື ລະຫັດຊ້ຳ — ປິດແລ້ວເປີດໃໝ່ເພື່ອໂຫຼດຄ່າລ່າສຸດ"
              : "ລະຫັດນີ້ມີຢູ່ແລ້ວໃນເມືອງນີ້ — ເລືອກລະຫັດອື່ນ"
            : describeError(res.error, res.response.status),
        );
      }
      return res.data.id;
    },
    onSuccess: onDone,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (code.trim() === "") {
      setError("ກະລຸນາໃສ່ລະຫັດ");
      return;
    }
    if (nameLO.trim() === "") {
      setError("ກະລຸນາໃສ່ຊື່ຕາແສງ");
      return;
    }
    setError(null);
    save.mutate();
  }

  const title = item ? "ແກ້ໄຂຕາແສງ" : "ເພີ່ມຕາແສງ";
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <form className="modal modal--sm" onSubmit={submit}>
        <div className="modal__header">
          <h2 className="modal__title">{title}</h2>
          <button type="button" className="btn-outline" onClick={onClose} aria-label="ປິດ">
            <X size={16} />
          </button>
        </div>
        <div className="stack">
          <div>
            <label htmlFor="tasaeng-code" className="form-field-label">
              ລະຫັດ<span className="required"> *</span>
            </label>
            <input
              id="tasaeng-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              maxLength={32}
              className="input-field num"
            />
          </div>
          <div>
            <label htmlFor="tasaeng-name" className="form-field-label">
              ຊື່ຕາແສງ<span className="required"> *</span>
            </label>
            <input
              id="tasaeng-name"
              value={nameLO}
              onChange={(e) => {
                setNameLO(e.target.value);
              }}
              maxLength={200}
              className="input-field"
            />
          </div>
          {item ? null : <p className="caption">ບັນທຶກແລ້ວຈຶ່ງເລືອກບ້ານທີ່ຂຶ້ນກັບຕາແສງນີ້ຢູ່ທາງຂວາ</p>}
        </div>
        {error ? <div className="alert-error">{error}</div> : null}
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={save.isPending}>
            ຍົກເລີກ
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກ"}
          </button>
        </div>
      </form>
    </div>
  );
}

/**
 * เพิ่มบ้านใหม่ในเมืองที่เลือก (data/villages.csv ยังมีบ้านไม่ครบทุกเมือง) หรือแก้รหัส/ชื่อบ้านเดิม
 * แก้ไม่ย้ายเมือง จึงไม่หลุดจากตาแสง (ย้ายเมือง = server ตัดออกจากตาแสงเดิม)
 */
function VillageModal({
  districtID,
  item,
  onClose,
  onDone,
}: {
  districtID: string;
  item: RefItem | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [code, setCode] = useState(item?.code ?? "");
  const [nameLO, setNameLO] = useState(item?.name_lo ?? "");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body = { district_id: item?.parent_id ?? districtID, code: code.trim(), name_lo: nameLO.trim() };
      const headers = authHeaders();
      const { error: err, response } = item
        ? await api.PUT("/v1/admin/villages/{id}", {
            params: { path: { id: item.id }, header: { "If-Match": String(item.version) } },
            body,
            headers,
          })
        : await api.POST("/v1/admin/villages", { body, headers });
      if (err) {
        throw new Error(
          response.status === 409
            ? item
              ? "ມີຄົນແກ້ໄຂບ້ານນີ້ກ່ອນແລ້ວ ຫຼື ລະຫັດຊ້ຳ — ປິດແລ້ວເປີດໃໝ່ເພື່ອໂຫຼດຄ່າລ່າສຸດ"
              : "ລະຫັດບ້ານນີ້ມີຢູ່ແລ້ວ — ເລືອກລະຫັດອື່ນ"
            : describeError(err, response.status),
        );
      }
    },
    onSuccess: onDone,
    onError: (e: Error) => {
      setError(e.message);
    },
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    if (code.trim() === "" || nameLO.trim() === "") {
      setError("ກະລຸນາໃສ່ລະຫັດ ແລະ ຊື່ບ້ານ");
      return;
    }
    setError(null);
    save.mutate();
  }

  const title = item ? "ແກ້ໄຂບ້ານ" : "ເພີ່ມບ້ານ";
  return (
    <div className="modal-overlay" role="dialog" aria-modal="true" aria-label={title}>
      <form className="modal modal--sm" onSubmit={submit}>
        <div className="modal__header">
          <h2 className="modal__title">{title}</h2>
          <button type="button" className="btn-outline" onClick={onClose} aria-label="ປິດ">
            <X size={16} />
          </button>
        </div>
        <div className="stack">
          <div>
            <label htmlFor="village-code" className="form-field-label">
              ລະຫັດບ້ານ<span className="required"> *</span>
            </label>
            <input
              id="village-code"
              value={code}
              onChange={(e) => {
                setCode(e.target.value);
              }}
              maxLength={32}
              className="input-field num"
            />
          </div>
          <div>
            <label htmlFor="village-name" className="form-field-label">
              ຊື່ບ້ານ<span className="required"> *</span>
            </label>
            <input
              id="village-name"
              value={nameLO}
              onChange={(e) => {
                setNameLO(e.target.value);
              }}
              maxLength={200}
              className="input-field"
            />
          </div>
        </div>
        {error ? <div className="alert-error">{error}</div> : null}
        <div className="modal__actions">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={save.isPending}>
            ຍົກເລີກ
          </button>
          <button type="submit" className="btn-primary" disabled={save.isPending}>
            {save.isPending ? "ກຳລັງບັນທຶກ…" : "ບັນທຶກ"}
          </button>
        </div>
      </form>
    </div>
  );
}
